import React, { useMemo, useState } from "react";

import {
  faCube,
  faLock,
  faLockOpen,
  faQuestion,
} from "@fortawesome/pro-regular-svg-icons";
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { capitalize, omitBy } from "lodash";
import { twJoin } from "tailwind-merge";
import { useBoolean } from "usehooks-ts";
import { validate as validateUUID } from "uuid";

import { GenericObject } from "src/api/flowTypes";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { DataValue } from "src/base-components/DataList";
import { Divider } from "src/base-components/Divider";
import { FloatingWindow } from "src/base-components/FloatingWindow/FloatingWindow";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { Cell, TableComp } from "src/base-components/Table";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { Address } from "src/clients/entities/api";
import { PropertyDefinitionOutputTypeEnum } from "src/clients/features-control";
import { EntityFeatureValue } from "src/clients/features-retrieve/api";
import { ObjectDetailPane } from "src/dataTable/ObjectDetailPane";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon, IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { ClampedItems } from "src/entities/entityView/ClampedItems";
import { EntityDetailsWindowPill } from "src/entities/entityView/EntityDetailsWindowPill";
import { LinkedEntityPill } from "src/entities/entityView/LinkedEntityPill";
import {
  findSchema,
  getSchemaIcon,
  getTableData,
  transformEnumValue,
} from "src/entities/entityView/utils";
import {
  Cardinality,
  CoordinatesPropertyValue,
  EntityFieldType,
  EntityFieldUrlType,
  EntityPropertyValue,
  EntityResource,
  EntityResourceReservedKeys,
  EntitySchemaProperty,
  EntitySchemaResource,
  FilePropertyValue,
  IpAddressPropertyValue,
  RelationshipProperty,
  TableRow,
  useEntity,
  useEntitySchemas,
  useRelatedEntitiesForCopy,
  useRevealEntityField,
} from "src/entities/queries";
import {
  renderFile,
  renderIpAddress,
  renderLatLong,
  renderUrl,
} from "src/entitiesEventsCommon/propertyRenderers";
import {
  SensitiveField,
  SensitiveFieldValue,
} from "src/entitiesEventsCommon/SensitiveField";
import { RelationIcon } from "src/eventsCatalogue/SchemaEditor/RelationIcons";
import { PROPERTY_TYPES_TO_ICONS } from "src/eventsCatalogue/SchemaEditor/utils";
import { EnumPill } from "src/eventsCatalogue/tableConfig";
import { useEntityFeatures } from "src/featureCatalogue/queries";
import { useCapabilities } from "src/hooks/useCapabilities";
import { Environment, useEnvironment } from "src/hooks/useEnvironment";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { useIsFWDOrAdmin } from "src/store/AuthStore";
import { copyTextToClipboard } from "src/utils/clipboard";
import { stringifiedObjectSpeakPython } from "src/utils/speakPython";

const columnHelper = createColumnHelper<TableRow>();

type RenderValueFnProps = {
  property: EntitySchemaProperty;
  propertyValue: EntityPropertyValue;
  isWindowPill: boolean;
  foreginKeyToPrimaryProperty: EntityResource["_fk_pp"];
  maxDisplayLines?: 1 | 2 | 3 | 4 | 5 | 6;
  environment: Environment;
};

type RenderByTypeFnProps = Omit<RenderValueFnProps, "propertyValue"> & {
  propertyValue: Exclude<EntityPropertyValue, string[]>;
};

type TableActions = {
  getForeignKeyMap: () => EntityResource["_fk_pp"];
};

export const JsonContent: React.FC<{
  content: GenericObject;
  title: string;
}> = ({ title, content }) => {
  const { value: isOpen, setTrue, setFalse } = useBoolean(false);
  const stringValue = JSON.stringify(content);

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setTrue()}
      onMouseLeave={() => setFalse()}
    >
      <FloatingWindow
        button={
          <div
            className={twJoin(
              "h-full w-80 max-w-full cursor-default",
              isOpen && "bg-indigo-100",
            )}
          >
            <div className="truncate">
              {stringifiedObjectSpeakPython(stringValue)}
            </div>
          </div>
        }
        draggable={false}
        isOpen={isOpen}
        maximizable={false}
        pinnable={false}
        title={title}
        onClose={setFalse}
        onCopy={() => copyTextToClipboard(stringValue)}
      >
        <ObjectDetailPane
          innerDimensionClass="h-full"
          jsonObject={JSON.parse(stringValue) as GenericObject}
        />
      </FloatingWindow>
    </div>
  );
};

const getType = (info: CellContext<TableRow, EntityPropertyValue>) =>
  info.row.original._type;

const renderRelation = ({
  property,
  propertyValue,
  isWindowPill,
  foreginKeyToPrimaryProperty,
  environment,
}: RenderByTypeFnProps & { propertyValue: string }) => {
  const Comp = isWindowPill ? EntityDetailsWindowPill : LinkedEntityPill;
  const name =
    foreginKeyToPrimaryProperty?.[`${property._rel_schema}.${propertyValue}`] ??
    propertyValue;

  return (
    <div className="mr-1 max-w-full">
      <Comp
        entityIcon={faCube}
        environment={environment}
        fullWidth={false}
        id={propertyValue}
        name={name}
        schemaId={property._rel_schema!}
      />
    </div>
  );
};

export const renderAddress = (
  propertyValue: Address,
  maxDisplayLines = 3 as RenderValueFnProps["maxDisplayLines"],
) => {
  const addressString = [
    propertyValue?.name,
    propertyValue?.address_1,
    propertyValue?.address_2,
    propertyValue?.city,
    propertyValue?.region,
    propertyValue?.zipcode,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <DataValue
      field=""
      maxDisplayLines={maxDisplayLines}
      value={addressString}
    />
  );
};

const renderEnumOrList = (
  property: EntitySchemaProperty,
  propertyValue: Exclude<EntityPropertyValue, string[]>,
) => {
  const enumValue = transformEnumValue(
    property._values ?? [],
    propertyValue as string,
  );
  return (
    <div className="mr-1 overflow-hidden">
      {enumValue ? (
        <EnumPill enumValue={enumValue} />
      ) : (
        <Pill size="sm" variant="gray" fullWidth>
          <Pill.Text>
            {typeof propertyValue === "string" ? (
              propertyValue
            ) : (
              <DataValue field="" maxDisplayLines={3} value={propertyValue} />
            )}
          </Pill.Text>
        </Pill>
      )}
    </div>
  );
};

const renderByType = ({
  property,
  propertyValue,
  isWindowPill,
  foreginKeyToPrimaryProperty,
  maxDisplayLines,
  environment,
}: RenderByTypeFnProps) => {
  const { _type: propertyType } = property;

  if (propertyValue === null || propertyValue === undefined) {
    return null;
  }
  if (
    propertyType === PropertyDefinitionOutputTypeEnum.RELATION &&
    property._rel_schema &&
    typeof propertyValue === "string"
  ) {
    return renderRelation({
      property,
      propertyValue,
      isWindowPill,
      foreginKeyToPrimaryProperty,
      environment,
    });
  }

  if (typeof propertyValue === "string" && validateUUID(propertyValue)) {
    return <TruncatedIdPill id={propertyValue} />;
  }

  switch (propertyType) {
    case PropertyDefinitionOutputTypeEnum.URL:
      return renderUrl(propertyValue as unknown as EntityFieldUrlType);

    case PropertyDefinitionOutputTypeEnum.ADDRESS:
      return renderAddress(
        propertyValue as Address,
        maxDisplayLines as RenderValueFnProps["maxDisplayLines"],
      );

    case PropertyDefinitionOutputTypeEnum.IP_ADDRESS:
      return renderIpAddress(propertyValue as IpAddressPropertyValue);

    case PropertyDefinitionOutputTypeEnum.LAT_LONG:
      return renderLatLong(propertyValue as CoordinatesPropertyValue);

    case PropertyDefinitionOutputTypeEnum.FILE:
      return renderFile(propertyValue as FilePropertyValue);
  }

  if (typeof propertyValue === "object") {
    return (
      <JsonContent content={propertyValue} title={capitalize(propertyType)} />
    );
  }

  if (["enum", "tags", "list"].includes(propertyType)) {
    return renderEnumOrList(property, propertyValue);
  }

  const disableTooltip = propertyType === "boolean" || propertyValue === null;

  return (
    <div
      className="max-w-full cursor-text"
      onClick={(e) => e.stopPropagation()}
    >
      <DataValue
        disableTooltip={disableTooltip}
        field=""
        maxDisplayLines={maxDisplayLines}
        value={propertyValue}
      />
    </div>
  );
};

export const renderValue = ({
  property,
  propertyValue,
  isWindowPill,
  foreginKeyToPrimaryProperty,
  maxDisplayLines = 3,
  environment,
}: RenderValueFnProps) => {
  if (Array.isArray(propertyValue)) {
    const hasObjectItems = propertyValue.some(
      (val) => val !== null && typeof val === "object",
    );

    if (hasObjectItems) {
      return (
        <JsonContent
          content={propertyValue as unknown as GenericObject}
          title="Array"
        />
      );
    }
    const mapped = propertyValue.map((val) => (
      <React.Fragment key={String(val)}>
        {renderByType({
          property,
          propertyValue: val,
          isWindowPill,
          foreginKeyToPrimaryProperty,
          maxDisplayLines,
          environment,
        })}
      </React.Fragment>
    ));

    return (
      <div className="flex items-center gap-1">
        <ClampedItems items={mapped} maxVisibleItems={1} />
      </div>
    );
  }

  return renderByType({
    property,
    propertyValue,
    isWindowPill,
    foreginKeyToPrimaryProperty,
    maxDisplayLines,
    environment,
  });
};

export const TypeIcon = ({
  type,
  cardinality,
}: {
  type: EntityFieldType;
  cardinality: Cardinality | undefined;
}) => {
  if (type === "relation") {
    return (
      <div className="shrink-0">
        <RelationIcon cardinality={cardinality ?? Cardinality.ONE} size="2xs" />
      </div>
    );
  }

  return <Icon icon={PROPERTY_TYPES_TO_ICONS[type] ?? faQuestion} size="2xs" />;
};

type EntityFieldProps = {
  value: unknown;
  isLoading?: boolean;
  isSensitive?: boolean;
  render: (value: unknown) => React.ReactNode;
  onToggle: () => void;
  isRevealed: boolean;
};

const EntityField: React.FC<EntityFieldProps> = ({
  value,
  isLoading = false,
  isSensitive = false,
  render,
  onToggle,
  isRevealed,
}) => {
  const { sensitiveFields } = useCapabilities();
  const isRevealable = isSensitive && sensitiveFields.canReveal;

  return (
    <div className="flex w-full items-center gap-x-1">
      <div className="min-w-0 flex-1">
        {isLoading ? (
          <SkeletonPlaceholder height="h-4" width="w-32" />
        ) : (
          render(value)
        )}
      </div>
      {value !== null && isRevealable && !isLoading && (
        <Tooltip
          body={
            !isRevealed &&
            "Unmask the sensitive field. A log of this action will be recorded in the Audit Log. The field will revert back to being masked when you leave the page"
          }
          placement="right"
          title={isRevealed ? "Hide sensitive data" : "Reveal masked field"}
          asChild
        >
          <IconButton
            icon={isRevealed ? faLockOpen : faLock}
            size="xs"
            onClick={onToggle}
          />
        </Tooltip>
      )}
    </div>
  );
};

type EntityDetailsCellProps = {
  info: CellContext<TableRow, EntityPropertyValue>;
  isWindowPill: boolean;
  fkMap: EntityResource["_fk_pp"];
  environment: Environment;
};

const EntityDetailsCell: React.FC<EntityDetailsCellProps> = ({
  info,
  isWindowPill,
  fkMap,
  environment,
}) => {
  const isSensitive = !!info.row.original._sensitive;
  const baseValue = info.getValue();
  const field = info.row.original._property_key;
  const [isRevealed, setIsRevealed] = useState(false);

  const schemaId = info.row.original._schema;
  const entityId = info.row.original._id;
  const hasValidIds = Boolean(schemaId && entityId);

  const { data: revealData, isLoading } = useRevealEntityField({
    env: environment,
    schemaId,
    entityId,
    field,
    enabled: isSensitive && isRevealed && hasValidIds,
  });

  if (info.row.original.isId) {
    return (
      <Cell info={info}>
        <TruncatedIdPill id={info.getValue() as string} />
      </Cell>
    );
  }

  const renderEntityValue = (value: unknown) => (
    <div className="min-w-0 flex-1 whitespace-normal break-words">
      {isSensitive ? (
        <SensitiveField
          displayInfoIcon={false}
          displayJsonFormat={false}
          isRevealed={isRevealed}
          propertyName={info.row.original._display_name}
          value={value as SensitiveFieldValue}
        />
      ) : (
        renderValue({
          property: info.row.original,
          propertyValue: value as EntityPropertyValue,
          isWindowPill,
          foreginKeyToPrimaryProperty: fkMap,
          environment,
        })
      )}
    </div>
  );

  const handleToggle = () => {
    setIsRevealed((prevRevealed) => !prevRevealed);
  };

  return (
    <Cell className="h-auto max-w-full" info={info}>
      <EntityField
        isLoading={isLoading}
        isRevealed={isRevealed}
        isSensitive={isSensitive && hasValidIds}
        render={renderEntityValue}
        value={isRevealed ? revealData?.value : baseValue}
        onToggle={handleToggle}
      />
    </Cell>
  );
};

export const getEntityDetailsColumns = (
  isWindowPill: boolean,
  environment: Environment,
) => [
  columnHelper.accessor("_display_name", {
    header: () => null,
    size: 129,
    maxSize: 129,
    cell: (info: CellContext<TableRow, EntityPropertyValue>) => {
      const type = getType(info);
      const cardinality = info.row.original._cardinality;
      return (
        <Cell className="items-start" info={info}>
          <TypeIcon cardinality={cardinality} type={type} />
          <Tooltip title={info.row.original._display_name} asChild>
            <p className="truncate capitalize">
              {info.row.original._display_name}
            </p>
          </Tooltip>
        </Cell>
      );
    },
  }),
  columnHelper.accessor("value", {
    header: () => null,
    size: 189,
    maxSize: 189,
    cell: (info: CellContext<TableRow, EntityPropertyValue>) => {
      const fkMap = (
        info.table.options.meta?.actions as TableActions
      )?.getForeignKeyMap();
      return (
        <EntityDetailsCell
          environment={environment}
          fkMap={fkMap}
          info={info}
          isWindowPill={isWindowPill}
        />
      );
    },
  }),
];

type EntityDetailsSidePaneProps = {
  entityId: string;
  schemaId: string;
  showHeader?: boolean;
  isWindowPill?: boolean;
  environment: Environment;
};

type EntityDetailsHeadeProps = {
  entitySchema: EntitySchemaResource;
  entity: EntityResource;
};

export const getRelationshipPropertiesFromEntity = (
  entitySchema: EntitySchemaResource,
  entity: EntityResource,
): RelationshipProperty[] => {
  return Object.entries(entitySchema.properties)
    .filter(
      ([propertyName, property]) =>
        property._type === "relation" &&
        property._rel_schema &&
        entity.properties[propertyName] != null,
    )
    .map(([propertyName, property]) => {
      const relationValue = entity.properties[propertyName];
      const entityIds = Array.isArray(relationValue)
        ? relationValue.filter((id) => id != null).map(String)
        : [String(relationValue)];

      return {
        propertyName,
        entityIds,
        targetSchema: property._rel_schema!,
      };
    })
    .filter(({ entityIds }) => entityIds.length > 0);
};

const cleanData = (data: Record<string, unknown>): Record<string, unknown> => {
  return {
    ...omitBy(data, (_value, key) => key.startsWith("_")),
    id: data._id,
  };
};

type RelatedEntitiesQueryResult = {
  data:
    | {
        data: EntityResourceReservedKeys;
        propertyName: string;
        entityId: string;
      }
    | undefined;
};

export const constructEntityJson = (
  entity: EntityResource,
  entitySchema: EntitySchemaResource,
  relatedEntitiesQueries: RelatedEntitiesQueryResult[],
  entityFeatures: EntityFeatureValue[],
  includeComputedFeatures: boolean,
): Record<string, unknown> => {
  const cleanedEntity = cleanData({
    ...entity.properties,
    _id: entity._id,
  });

  const entitiesByProperty = relatedEntitiesQueries.reduce<
    Record<string, Record<string, unknown>[]>
  >((acc, query) => {
    if (query.data) {
      const { propertyName, entityId, data: entityData } = query.data;
      const cleanedEntityData = cleanData({
        ...entityData,
        _id: entityId,
      });

      acc[propertyName] = acc[propertyName] || [];
      acc[propertyName].push(cleanedEntityData);
    }
    return acc;
  }, {});

  const relatedEntities: Record<string, unknown> = {};
  Object.entries(entitiesByProperty).forEach(([propertyName, entities]) => {
    if (entities.length === 0) return;

    const property = entitySchema.properties[propertyName];
    relatedEntities[propertyName] =
      property?._cardinality === Cardinality.ONE ? entities[0] : entities;
  });

  const baseObject = {
    ...cleanedEntity,
    ...relatedEntities,
  };

  if (!includeComputedFeatures) {
    return baseObject;
  }

  const featuresObject = entityFeatures.reduce(
    (acc, feature) => {
      acc[feature.key] = feature.value;
      return acc;
    },
    {} as Record<string, unknown>,
  );

  return {
    ...baseObject,
    features: featuresObject,
  };
};

type CopyEntityJsonButtonProps = {
  entity: EntityResource;
  entitySchema: EntitySchemaResource;
};

const CopyEntityJsonButton: React.FC<CopyEntityJsonButtonProps> = ({
  entity,
  entitySchema,
}) => {
  const { workspace } = useWorkspaceContext();
  const { featuresEventsEnabled } = useWorkspaceFeatureGates();
  const [env] = useEnvironment();
  const { data: entityFeatures } = useEntityFeatures(
    env,
    entity._id,
    entity._schema,
    { enabled: featuresEventsEnabled },
  );

  const relationshipProperties = useMemo(
    () => getRelationshipPropertiesFromEntity(entitySchema, entity),
    [entitySchema, entity],
  );

  const relatedEntitiesQueries = useRelatedEntitiesForCopy({
    relationshipProperties,
    baseUrl: workspace.base_url ?? "",
    env,
  });

  const entityJson = useMemo(() => {
    return JSON.stringify(
      constructEntityJson(
        entity,
        entitySchema,
        relatedEntitiesQueries,
        entityFeatures?.data ?? [],
        featuresEventsEnabled,
      ),
      null,
      2,
    );
  }, [
    entity,
    entitySchema,
    relatedEntitiesQueries,
    entityFeatures?.data,
    featuresEventsEnabled,
  ]);

  return (
    <CopyTextIcon
      dataLoc="copy-entity-data"
      feedback="inline"
      tooltip="Copy Entity as JSON"
      tooltipPlacement="bottom"
      value={entityJson}
    />
  );
};

const formatPropertyValue = (property: unknown): string => {
  if (property === null || property === undefined) {
    return "";
  }
  return typeof property === "object"
    ? JSON.stringify(property)
    : String(property);
};

const EntityDetailsHeader: React.FC<EntityDetailsHeadeProps> = ({
  entitySchema,
  entity,
}) => {
  const isFWDOrAdmin = useIsFWDOrAdmin();
  const [primaryProperty, secondaryProperty] = [
    entity.properties[entitySchema._primary_property],
    entity.properties[entitySchema._secondary_property],
  ];

  const primaryDisplayValue = formatPropertyValue(primaryProperty);
  const secondaryDisplayValue = formatPropertyValue(secondaryProperty);

  return (
    <div className="flex justify-between gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200">
        <Icon icon={getSchemaIcon(entitySchema)} size="md" />
      </div>

      <div className="min-w-0 flex-1">
        {primaryDisplayValue && (
          <Tooltip title={primaryDisplayValue} asChild>
            <p className="line-clamp-3 font-inter-normal-13px">
              {primaryDisplayValue}
            </p>
          </Tooltip>
        )}
        {secondaryDisplayValue && (
          <Tooltip title={secondaryDisplayValue} asChild>
            <p className="line-clamp-3 font-inter-medium-11px text-gray-500">
              {secondaryDisplayValue}
            </p>
          </Tooltip>
        )}
      </div>

      <div>
        {isFWDOrAdmin && (
          <CopyEntityJsonButton entity={entity} entitySchema={entitySchema} />
        )}
      </div>
    </div>
  );
};

export const EntityDetailsSidePane: React.FC<EntityDetailsSidePaneProps> = ({
  entityId,
  schemaId,
  showHeader = true,
  isWindowPill = false,
  environment,
}) => {
  const { workspace } = useWorkspaceContext();
  const { data: entitySchemas, isLoading: isLoadingSchemas } = useEntitySchemas(
    {
      baseUrl: workspace.base_url ?? "",
    },
  );
  const { data: entityData, isLoading: isLoadingEntity } = useEntity({
    schema: schemaId,
    env: environment,
    entityId,
    enabled: !isLoadingSchemas,
  });

  const [tableData, entitySchema] = useMemo(() => {
    const entitySchema = findSchema(schemaId, entitySchemas);

    return [getTableData(entitySchema, entityData), entitySchema];
  }, [schemaId, entityData, entitySchemas]);

  const tableActions: TableActions = useMemo(
    () => ({
      getForeignKeyMap: () => entityData?._fk_pp,
    }),
    [entityData?._fk_pp],
  );

  const columns = useMemo(
    () => getEntityDetailsColumns(isWindowPill, environment),
    [isWindowPill, environment],
  );

  if (!isLoadingEntity && !entityData) {
    return (
      <EmptyState
        description="Unable to load entity"
        headline="Error"
        icon={faCube}
        variant="default"
      />
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {showHeader && entitySchema && entityData && (
        <>
          <EntityDetailsHeader
            entity={entityData}
            entitySchema={entitySchema}
          />
          <Divider spacing="my-4" />
        </>
      )}
      <div className="decideScrollbar flex-1">
        <TableComp
          actions={tableActions}
          columns={columns}
          data={tableData}
          frameClassName="[&_thead_tr]:hidden [&_table]:border-b-0"
          isLoading={isLoadingSchemas || isLoadingEntity}
          rowClassName="align-top"
          variant="compact"
        />
      </div>
    </div>
  );
};
